import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { setError } from '@/actions';
import { cardPaymentAttempt } from '@/actions/payment';
import type { BasisTheoryClient } from 'services/basisTheory';
import errorMonitoring from '@/errorMonitoring';
import basisTheoryCheckoutEngine from '@/payments/core/engines/basisTheory';
import type {
  BasisTheoryCardElements,
  BasisTheoryElementsStatus,
} from '@/hooks/libs/useBasisTheoryElements';
import { createBasisTheoryPaymentData } from './basisTheorySubmission';

type Dispatch = (action: unknown) => unknown;
type BasisTheoryCheckoutStatus = BasisTheoryElementsStatus | 'incomplete' | 'submitting';

interface UseBasisTheorySubmitOptions {
  active: boolean;
  canSaveCard: boolean;
  cardBrand: string;
  clientRef: MutableRefObject<BasisTheoryClient | null>;
  currency: string;
  dispatch: Dispatch;
  elementsRef: MutableRefObject<BasisTheoryCardElements | null>;
  holderName: string;
  isComplete: boolean;
  mountedRef: MutableRefObject<boolean>;
  monthlySelectedPlan: number;
  purchaseToken: string;
  saveCard: boolean;
  status: BasisTheoryElementsStatus;
}

const createSetErrorAction: typeof setError = setError;

/**
 * Tokenize and submit the current Basis Theory card.
 *
 * @param {object} options - Hook options.
 * @param {boolean} options.active - Whether Basis Theory currently owns card submission.
 * @param {boolean} options.canSaveCard - Whether the current user can save the card.
 * @param {string} options.cardBrand - Detected card brand.
 * @param {object} options.clientRef - Basis Theory client ref.
 * @param {string} options.currency - Purchase currency.
 * @param {Function} options.dispatch - Redux dispatch function.
 * @param {object} options.elementsRef - Secure card elements ref.
 * @param {string} options.holderName - Current cardholder name.
 * @param {boolean} options.isComplete - Whether every secure card field is valid and complete.
 * @param {object} options.mountedRef - Mounted-state ref.
 * @param {number} options.monthlySelectedPlan - Selected installment count.
 * @param {string} options.purchaseToken - Purchase identifier.
 * @param {boolean} options.saveCard - Whether the user requested card storage.
 * @param {string} options.status - Secure-elements lifecycle status.
 * @returns {boolean} Whether tokenization is in progress.
 */
function useBasisTheorySubmit({
  active,
  canSaveCard,
  cardBrand,
  clientRef,
  currency,
  dispatch,
  elementsRef,
  holderName,
  isComplete,
  mountedRef,
  monthlySelectedPlan,
  purchaseToken,
  saveCard,
  status,
}: UseBasisTheorySubmitOptions): boolean {
  const submittingRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!active || submittingRef.current || status !== 'ready') return;
    const normalizedHolderName = holderName.trim();
    if (!normalizedHolderName) {
      dispatch(
        createSetErrorAction(
          200,
          'card_holder_name_error',
          'warning',
          false,
          undefined,
          undefined,
          undefined,
          'global',
          undefined,
        ),
      );
      return;
    }
    if (!isComplete) {
      dispatch(
        createSetErrorAction(
          200,
          'check_your_card_details',
          'warning',
          false,
          undefined,
          undefined,
          undefined,
          'global',
          undefined,
        ),
      );
      return;
    }

    const submissionAttemptId = uuidv4();
    submittingRef.current = true;
    setIsSubmitting(true);
    let submittedElements: BasisTheoryCardElements | null = null;
    try {
      const client = clientRef.current;
      const elements = elementsRef.current;
      if (!client || !elements) throw new Error('card_elements_unavailable');
      submittedElements = elements;

      const payment = await createBasisTheoryPaymentData({
        canSaveCard,
        cardBrand,
        client,
        currency,
        elements,
        holderName: normalizedHolderName,
        monthlySelectedPlan,
        purchaseToken,
        saveCard,
        submissionAttemptId,
      });
      if (activeRef.current && mountedRef.current && elementsRef.current === submittedElements) {
        dispatch(
          cardPaymentAttempt(payment.purchaseToken, payment.cardValues, {
            ...payment.paymentInfo,
            cardCheckoutMode: 'basis_theory',
          }),
        );
      }
    } catch (error) {
      errorMonitoring.notify({
        error,
        info: { correlationId: submissionAttemptId, stage: 'new_card_tokenization' },
      });
      if (activeRef.current && mountedRef.current && elementsRef.current === submittedElements) {
        dispatch(
          createSetErrorAction(
            200,
            'error_generating_payment',
            'warning',
            false,
            undefined,
            undefined,
            undefined,
            'global',
            undefined,
          ),
        );
      }
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setIsSubmitting(false);
    }
  }, [
    active,
    canSaveCard,
    cardBrand,
    clientRef,
    currency,
    dispatch,
    elementsRef,
    holderName,
    isComplete,
    mountedRef,
    monthlySelectedPlan,
    purchaseToken,
    saveCard,
    status,
  ]);

  const submitRef = useRef(submit);
  submitRef.current = submit;
  const registrationRef = useRef<
    ReturnType<typeof basisTheoryCheckoutEngine.registerSubmitHandler> | undefined
  >();

  useEffect(() => {
    if (!active) return undefined;
    /** Submit the latest rendered form state from Checkout's shared payment button. */
    function submitCurrentForm() {
      return submitRef.current();
    }

    const registration = basisTheoryCheckoutEngine.registerSubmitHandler(
      submitCurrentForm,
      'loading',
    );
    registrationRef.current = registration;

    return () => {
      registration.unregister();
      registrationRef.current = undefined;
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const formIsReady = status === 'ready' && isComplete && Boolean(holderName.trim());
    let checkoutStatus: BasisTheoryCheckoutStatus = status;
    if (status === 'ready' && !formIsReady) checkoutStatus = 'incomplete';
    if (isSubmitting) checkoutStatus = 'submitting';
    registrationRef.current?.updateStatus(checkoutStatus);
  }, [active, holderName, isComplete, isSubmitting, status]);

  return isSubmitting;
}

export { useBasisTheorySubmit };
